// Iter 82.1 / Phase E1.1 — Per-employee leave balance detail page.
// Surfaces:
//  • Employee header (code, name, dept, designation)
//  • Per leave type: current balance + accrued/taken/encashed/adjusted breakdown
//  • Ledger drill-down for the selected leave type with movements timeline
//  • Adjust (delta + reason + remarks) and Encash (days + remarks) dialogs
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Plus, Coins, DownloadSimple, PencilSimple, ClockCounterClockwise, Warning } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

// Iter 110 · Slice 1 (P1.A) — Reason vocabulary is now server-side.
// The component fetches `manual_adjustment_reasons_detail` from
// /leave/ledger/meta and renders {code, label, description}. The
// hard-coded map below is a graceful fallback for entries the server
// hasn't labelled yet.
const REASON_LABELS = {
    correction_data_entry: "Data entry correction",
    policy_change: "Policy change",
    comp_off_credit: "Comp-off credit",
    bonus_credit: "Bonus credit",
    audit_correction: "Audit correction",
    transfer_balance: "Balance transfer",
    opening_balance_correction: "Opening balance correction",
    historical_correction: "Historical balance correction",
    hr_manual_comp_off_grant: "HR manual comp-off grant",
    other: "Other (specify in remarks)",
};

const MOVEMENT_TONE = {
    opening_balance: "bg-slate-100 text-slate-700",
    accrual: "bg-emerald-100 text-emerald-700",
    manual_adjustment: "bg-amber-100 text-amber-700",
    leave_taken: "bg-rose-100 text-rose-700",
    leave_cancelled: "bg-sky-100 text-sky-700",
    carry_forward: "bg-indigo-100 text-indigo-700",
    encashment: "bg-violet-100 text-violet-700",
    expiry: "bg-orange-100 text-orange-700",
    lop_charge: "bg-red-100 text-red-700",
    // Iter 110 movement-type toning.
    opening_balance_correction: "bg-slate-200 text-slate-800 border border-slate-300",
    accrual_correction: "bg-emerald-50 text-emerald-800 border border-emerald-200",
    taken_correction: "bg-rose-50 text-rose-800 border border-rose-200",
    carry_forward_adjustment: "bg-indigo-50 text-indigo-800 border border-indigo-200",
    comp_off_credit: "bg-teal-100 text-teal-800",
    comp_off_expiry: "bg-orange-100 text-orange-800",
};

// Iter 110 · P1.B — Balance-Correction targets. Each entry maps a
// human-friendly bucket name to (backend field key, movement_type_hint,
// delta sign convention). The dialog computes the delta needed to move
// the current bucket to the target value.
const BUCKET_TARGETS = [
    { key: "opening_balance", label: "Opening Balance", hint: "opening_balance_correction", flip: false, reason: "opening_balance_correction" },
    { key: "total_accrued_ytd", label: "Accrued YTD", hint: "accrual_correction", flip: false, reason: "audit_correction" },
    { key: "total_taken_ytd", label: "Consumed YTD (Taken)", hint: "taken_correction", flip: true, reason: "audit_correction" },
];

export default function LeaveBalanceEmployee() {
    const { empId } = useParams();
    const [search] = useSearchParams();
    const navigate = useNavigate();
    const { can } = useAuth();
    const [fys, setFys] = useState([]);
    const [fy, setFy] = useState(search.get("fy") || "");
    const [data, setData] = useState({ employee: null, balances: [] });
    const [ledgerData, setLedgerData] = useState({ ledger: [], balance: {} });
    const [selectedType, setSelectedType] = useState(null);
    const [reasonsDetail, setReasonsDetail] = useState([]); // Iter 110 · [{code, label, description}]
    const [adjustDraft, setAdjustDraft] = useState(null);
    const [encashDraft, setEncashDraft] = useState(null);
    const [openingDraft, setOpeningDraft] = useState(null);
    const [correctDraft, setCorrectDraft] = useState(null); // Iter 110 · P1.B Balance-Correction
    const [historicalConfirm, setHistoricalConfirm] = useState(""); // Iter 110 · P1.C phrase input
    // UAT · Opening Balance Provisioning fix — Migration Mode
    // detection for VISUAL prominence only. The credit action is
    // available to any leave:admin regardless of migration state.
    const [migrationActive, setMigrationActive] = useState(false);
    // Deep-link support — /admin/leave/balances/:empId?focus_pending=CL
    // opens the credit-opening-balance dialog for the given policy on
    // page load.
    const focusPendingCode = search.get("focus_pending");

    const loadMeta = useCallback(async () => {
        try {
            const [fyResp, metaResp] = await Promise.all([
                api.get("/leave/fiscal-year/list"),
                api.get("/leave/ledger/meta"),
            ]);
            setFys(fyResp.data.items || []);
            // Iter 110 · P1.A — prefer detail objects; fall back to codes.
            const detail = metaResp.data.manual_adjustment_reasons_detail;
            if (Array.isArray(detail) && detail.length) {
                setReasonsDetail(detail);
            } else {
                setReasonsDetail(
                    (metaResp.data.manual_adjustment_reasons || []).map((c) => ({
                        code: c, label: REASON_LABELS[c] || c, description: "",
                    })),
                );
            }
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load metadata"));
        }
    }, []);

    // Iter 110 · derive whether the current FY selection is historical
    // (any FY that isn't `is_current`). Triggers the confirmation UX.
    const currentFy = useMemo(() => (fys || []).find((f) => f.is_current), [fys]);
    const isHistoricalFy = useMemo(
        () => Boolean(fy && currentFy && fy !== currentFy.code),
        [fy, currentFy],
    );

    // Derive effective FY without setting state in an effect.
    const effectiveFy = useMemo(() => {
        if (fy) return fy;
        const current = (fys || []).find((f) => f.is_current);
        return current?.code || "";
    }, [fy, fys]);

    const reload = useCallback(async () => {
        if (!empId) return;
        try {
            const { data: payload } = await api.get(`/leave/balance/employee/${empId}`, {
                params: effectiveFy ? { fiscal_year: effectiveFy } : {},
            });
            setData(payload);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load employee balance"));
        }
    }, [empId, effectiveFy]);

    // Effective selected type — falls back to the first balance row when
    // the user hasn't explicitly picked one yet. Computed, not stored.
    const effectiveType = selectedType || (data.balances || [])[0]?.leave_type_code || null;
    const selectedBalance = (data.balances || []).find((b) => b.leave_type_code === effectiveType);

    const reloadLedger = useCallback(async () => {
        if (!empId || !effectiveType) return;
        try {
            const { data: payload } = await api.get(
                `/leave/balance/${empId}/${effectiveType}`,
                { params: effectiveFy ? { fiscal_year: effectiveFy } : {} },
            );
            setLedgerData(payload);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load ledger"));
        }
    }, [empId, effectiveType, effectiveFy]);

    useEffect(() => { Promise.resolve().then(loadMeta); }, [loadMeta]);
    useEffect(() => { reload(); }, [reload]);
    useEffect(() => { reloadLedger(); }, [reloadLedger]);

    // UAT · Opening Balance Provisioning fix — Migration Mode probe.
    // Purely visual — does NOT gate any action.
    useEffect(() => {
        let alive = true;
        api.get("/billing/migration-mode/status")
            .then((resp) => { if (alive) setMigrationActive(resp.data?.state === "active"); })
            .catch(() => { /* silent — never block on this */ });
        return () => { alive = false; };
    }, []);

    // Deep-link — when navigated with ?focus_pending=<CODE>, open the
    // opening-balance dialog for that policy once data is loaded.
    useEffect(() => {
        if (!focusPendingCode || !data.balances?.length) return;
        const target = data.balances.find(
            (b) => b.leave_type_code === focusPendingCode && !b.last_movement_at,
        );
        if (target && !openingDraft) {
            setOpeningDraft({ leave_type_code: target.leave_type_code, balance_days: "", remarks: "" });
        }
    }, [focusPendingCode, data.balances, openingDraft]);

    // Compute the pending-opening set (policies applicable to the
    // employee but with no ledger movement yet) for the prominent
    // top-of-page credit panel.
    const pendingOpeningPolicies = useMemo(
        () => (data.balances || []).filter((b) => !b.last_movement_at),
        [data.balances],
    );

    const submitAdjustment = async () => {
        if (!adjustDraft) return;
        if (!adjustDraft.delta_days || Number(adjustDraft.delta_days) === 0) {
            toast.error("Delta days must be non-zero");
            return;
        }
        if (!adjustDraft.reason) {
            toast.error("Reason is required");
            return;
        }
        if (!adjustDraft.remarks || adjustDraft.remarks.trim().length < 10) {
            toast.error("Remarks are required (min 10 characters)");
            return;
        }
        if (isHistoricalFy && historicalConfirm !== "CORRECT HISTORICAL BALANCE") {
            toast.error("Historical correction requires the exact confirmation phrase.");
            return;
        }
        try {
            await api.post("/leave/balance/adjust", {
                employee_id: empId,
                leave_type_code: adjustDraft.leave_type_code,
                delta_days: Number(adjustDraft.delta_days),
                reason: adjustDraft.reason,
                remarks: adjustDraft.remarks.trim(),
                fiscal_year: fy || undefined,
                corrects_entry_id: adjustDraft.corrects_entry_id || undefined,
                confirmation_phrase: isHistoricalFy ? "CORRECT HISTORICAL BALANCE" : undefined,
            });
            toast.success(
                isHistoricalFy
                    ? "Historical correction posted · cascade applied to downstream FYs"
                    : "Adjustment posted to ledger",
            );
            setAdjustDraft(null);
            setHistoricalConfirm("");
            reload();
            reloadLedger();
        } catch (err) {
            toast.error(formatApiError(err, "Adjustment failed"));
        }
    };

    // Iter 110 · P1.B — Balance-Correction: convert a "new target"
    // for a bucket into the delta needed to hit that target, then
    // post using the appropriate movement_type_hint.
    const submitBalanceCorrection = async () => {
        if (!correctDraft) return;
        const bucket = BUCKET_TARGETS.find((b) => b.key === correctDraft.bucket_key);
        if (!bucket) { toast.error("Choose a bucket to correct."); return; }
        const target = Number(correctDraft.target_value);
        if (Number.isNaN(target) || target < 0) { toast.error("Target value must be ≥ 0"); return; }
        const currentValue = Number(correctDraft.current_value || 0);
        // Determine the delta. Buckets that add to the balance flip=false;
        // "consumed" bucket subtracts from the balance so we flip.
        let delta;
        if (bucket.flip) {
            // total_taken_ytd increases when leave is taken. If target > current,
            // we're saying "increase consumed count by X", which means the
            // employee's *available* balance should decrease. But the ledger
            // convention `taken_correction` uses -delta on the bucket, so the
            // caller-facing delta_days is (current - target).
            delta = currentValue - target;
        } else {
            delta = target - currentValue;
        }
        if (delta === 0) { toast.error("Target equals current value — no correction needed."); return; }
        if (!correctDraft.remarks || correctDraft.remarks.trim().length < 10) {
            toast.error("Remarks are required (min 10 characters)"); return;
        }
        if (isHistoricalFy && historicalConfirm !== "CORRECT HISTORICAL BALANCE") {
            toast.error("Historical correction requires the exact confirmation phrase.");
            return;
        }
        try {
            await api.post("/leave/balance/adjust", {
                employee_id: empId,
                leave_type_code: correctDraft.leave_type_code,
                delta_days: Number(delta),
                reason: bucket.reason,
                remarks: correctDraft.remarks.trim(),
                fiscal_year: fy || undefined,
                movement_type_hint: bucket.hint,
                corrects_entry_id: correctDraft.corrects_entry_id || undefined,
                confirmation_phrase: isHistoricalFy ? "CORRECT HISTORICAL BALANCE" : undefined,
            });
            toast.success(
                `${bucket.label} corrected · delta ${delta > 0 ? "+" : ""}${delta.toFixed(2)} posted to ledger`,
            );
            setCorrectDraft(null);
            setHistoricalConfirm("");
            reload();
            reloadLedger();
        } catch (err) {
            toast.error(formatApiError(err, "Balance correction failed"));
        }
    };

    const submitEncashment = async () => {
        if (!encashDraft) return;
        if (!encashDraft.days || Number(encashDraft.days) <= 0) {
            toast.error("Days must be > 0");
            return;
        }
        if (!encashDraft.remarks || encashDraft.remarks.trim().length < 10) {
            toast.error("Remarks are required (min 10 characters)");
            return;
        }
        try {
            await api.post("/leave/balance/encash", {
                employee_id: empId,
                leave_type_code: encashDraft.leave_type_code,
                days: Number(encashDraft.days),
                remarks: encashDraft.remarks.trim(),
                fiscal_year: fy || undefined,
            });
            toast.success("Encashment posted to ledger");
            setEncashDraft(null);
            reload();
            reloadLedger();
        } catch (err) {
            toast.error(formatApiError(err, "Encashment failed"));
        }
    };

    const submitOpening = async () => {
        if (!openingDraft) return;
        if (openingDraft.balance_days === "" || Number(openingDraft.balance_days) < 0) {
            toast.error("Opening balance must be ≥ 0");
            return;
        }
        try {
            await api.post("/leave/balance/opening", {
                employee_id: empId,
                leave_type_code: openingDraft.leave_type_code,
                fiscal_year: fy,
                balance_days: Number(openingDraft.balance_days),
                reason: openingDraft.reason || "initial_seed",
                remarks: openingDraft.remarks || "",
            });
            toast.success("Opening balance posted");
            setOpeningDraft(null);
            reload();
            reloadLedger();
        } catch (err) {
            toast.error(formatApiError(err, "Opening balance failed"));
        }
    };

    const exportLedger = async () => {
        try {
            const params = { employee_id: empId };
            if (effectiveType) params.leave_type_code = effectiveType;
            if (fy) params.fiscal_year = fy;
            const { data: blob } = await api.get("/leave/ledger/export", { params, responseType: "blob" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const code = data.employee?.employee_code || empId;
            a.download = `leave-ledger-${code}-${effectiveType || "all"}-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err, "Export failed"));
        }
    };

    if (!data.employee) {
        return (
            <div className="px-6 sm:px-8 py-6" data-testid="leave-balance-employee-loading">
                <PageHeader eyebrow="Leave Operations" title="Loading…" />
            </div>
        );
    }

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="leave-balance-employee-page">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/leave/balances")} className="mb-2" data-testid="lbe-back-btn">
                <ArrowLeft size={14} className="mr-1" /> Back to Leave Balances
            </Button>
            <PageHeader
                eyebrow={`Leave Operations · ${data.employee.employee_code}`}
                title={data.employee.full_name}
                description={`Per-leave-type balances and movement history for fiscal year ${data.fiscal_year}.`}
                actions={(
                    <div className="flex gap-2 items-center">
                        <select
                            data-testid="lbe-fy-switcher"
                            className="border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                            value={fy}
                            onChange={(e) => setFy(e.target.value)}
                        >
                            {fys.map((f) => (<option key={f.id} value={f.code}>{f.code}</option>))}
                        </select>
                        <Button variant="outline" size="sm" onClick={exportLedger} data-testid="lbe-export-ledger-btn">
                            <DownloadSimple size={14} className="mr-1" /> Export Ledger
                        </Button>
                    </div>
                )}
            />

            {/* UAT · Opening Balance Provisioning fix — prominent
                top-of-page panel that surfaces every applicable policy
                with no ledger movement yet. Always visible to
                leave:admin. During Migration Mode, styled more
                prominently (amber tone). This is a DISCOVERY
                ACCELERATOR — the per-card 'Opening Balance' buttons
                below remain the same and are the canonical trigger. */}
            {can("leave:admin") && pendingOpeningPolicies.length > 0 && (
                <section
                    className={
                        "mt-5 rounded-xl border p-5 " +
                        (migrationActive
                            ? "bg-amber-50 border-amber-300 shadow-sm"
                            : "bg-sky-50 border-sky-200")
                    }
                    data-testid="lbe-pending-credits-panel"
                >
                    <div className="flex items-start gap-3">
                        <Warning
                            size={22}
                            className={
                                "mt-0.5 flex-shrink-0 " +
                                (migrationActive ? "text-amber-700" : "text-sky-700")
                            }
                        />
                        <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h3
                                    className={
                                        "text-sm font-semibold " +
                                        (migrationActive ? "text-amber-900" : "text-sky-900")
                                    }
                                    data-testid="lbe-pending-credits-title"
                                >
                                    {pendingOpeningPolicies.length} policy(ies) awaiting opening balance credit
                                </h3>
                                {migrationActive && (
                                    <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded">
                                        Migration Mode active
                                    </span>
                                )}
                            </div>
                            <p
                                className={
                                    "text-xs mt-1 " +
                                    (migrationActive ? "text-amber-800" : "text-sky-800")
                                }
                            >
                                Credit the opening balance for each applicable policy below.
                                One credit per (employee × leave type × fiscal year).
                                Duplicate credits are blocked; the balance appears in the
                                employee portal immediately.
                            </p>
                            <div
                                className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2"
                                data-testid="lbe-pending-credits-grid"
                            >
                                {pendingOpeningPolicies.map((b) => (
                                    <div
                                        key={b.leave_type_code}
                                        className="bg-white rounded-lg border border-slate-200 px-3 py-2 flex items-center justify-between gap-2"
                                        data-testid={`lbe-pending-credit-row-${b.leave_type_code}`}
                                    >
                                        <div className="min-w-0">
                                            <div className="text-xs font-mono font-semibold text-slate-900">
                                                {b.leave_type_code}
                                            </div>
                                            <div className="text-[11px] text-slate-500 truncate">
                                                {b.policy?.name || "—"}
                                            </div>
                                        </div>
                                        <Button
                                            size="sm"
                                            className={
                                                migrationActive
                                                    ? "bg-amber-600 hover:bg-amber-500 text-white h-7"
                                                    : "bg-sky-700 hover:bg-sky-600 text-white h-7"
                                            }
                                            onClick={() => setOpeningDraft({
                                                leave_type_code: b.leave_type_code,
                                                balance_days: "",
                                                remarks: "",
                                            })}
                                            data-testid={`lbe-pending-credit-btn-${b.leave_type_code}`}
                                        >
                                            <Plus size={12} className="mr-1" /> Credit
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
                {(data.balances || []).map((b) => {
                    const isActive = b.leave_type_code === effectiveType;
                    return (
                        <div
                            key={b.leave_type_code}
                            data-testid={`lbe-type-card-${b.leave_type_code}`}
                            onClick={() => setSelectedType(b.leave_type_code)}
                            className={`bg-white rounded-xl border p-4 cursor-pointer transition-all ${isActive ? "border-slate-900 shadow-md" : "border-slate-200 hover:border-slate-400"}`}
                        >
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{b.leave_type_code}</div>
                                    <div className="text-sm font-semibold text-slate-800 mt-0.5">{b.policy?.name || "—"}</div>
                                </div>
                                {b.policy?.encashment_allowed && (
                                    <Badge variant="outline" className="text-xs">Encashable</Badge>
                                )}
                            </div>
                            <div className="mt-3 text-3xl font-bold text-slate-900">{(b.current_balance ?? 0).toFixed(2)}</div>
                            <div className="text-xs text-slate-500">days available</div>
                            <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                                <div className="text-slate-500">Opening</div>
                                <div className="text-right text-slate-700">{(b.opening_balance ?? 0).toFixed(2)}</div>
                                <div className="text-slate-500">Accrued YTD</div>
                                <div className="text-right text-emerald-700">{(b.total_accrued_ytd ?? 0).toFixed(2)}</div>
                                <div className="text-slate-500">Taken YTD</div>
                                <div className="text-right text-rose-700">{(b.total_taken_ytd ?? 0).toFixed(2)}</div>
                                <div className="text-slate-500">Encashed YTD</div>
                                <div className="text-right text-amber-700">{(b.total_encashed_ytd ?? 0).toFixed(2)}</div>
                                <div className="text-slate-500">Adjusted YTD</div>
                                <div className="text-right text-slate-700">{(b.total_adjusted_ytd ?? 0).toFixed(2)}</div>
                                <div className="text-slate-500">Carry Fwd In</div>
                                <div className="text-right text-indigo-700">{(b.carry_forward_in ?? 0).toFixed(2)}</div>
                            </div>
                            {can("leave:admin") && isActive && (
                                <div className="mt-4 flex flex-wrap gap-2">
                                    <Button size="sm" variant="outline" data-testid={`lbe-adjust-btn-${b.leave_type_code}`}
                                        onClick={(e) => { e.stopPropagation(); setAdjustDraft({ leave_type_code: b.leave_type_code, delta_days: "", reason: "", remarks: "" }); }}>
                                        <Plus size={12} className="mr-1" /> Adjust
                                    </Button>
                                    <Button size="sm" variant="outline" data-testid={`lbe-correct-btn-${b.leave_type_code}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setCorrectDraft({
                                                leave_type_code: b.leave_type_code,
                                                bucket_key: "opening_balance",
                                                current_value: b.opening_balance ?? 0,
                                                target_value: (b.opening_balance ?? 0).toString(),
                                                remarks: "",
                                                _balance_row: b,
                                            });
                                        }}>
                                        <PencilSimple size={12} className="mr-1" /> Correct Bucket
                                    </Button>
                                    {b.policy?.encashment_allowed && (
                                        <Button size="sm" variant="outline" data-testid={`lbe-encash-btn-${b.leave_type_code}`}
                                            onClick={(e) => { e.stopPropagation(); setEncashDraft({ leave_type_code: b.leave_type_code, days: "", remarks: "" }); }}>
                                            <Coins size={12} className="mr-1" /> Encash
                                        </Button>
                                    )}
                                    {!b.last_movement_at && (
                                        <Button size="sm" variant="outline" data-testid={`lbe-opening-btn-${b.leave_type_code}`}
                                            onClick={(e) => { e.stopPropagation(); setOpeningDraft({ leave_type_code: b.leave_type_code, balance_days: "", remarks: "" }); }}>
                                            Opening Balance
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
                {data.balances.length === 0 && (
                    <div className="col-span-3 text-center text-sm text-slate-400 py-10">
                        No leave policies apply to this employee.
                    </div>
                )}
            </div>

            <section className="mt-6 bg-white rounded-xl border border-slate-200" data-testid="lbe-ledger-section">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-800">
                        Ledger History {effectiveType ? `· ${effectiveType}` : ""}
                    </h3>
                    {selectedBalance && (
                        <div className="text-xs text-slate-500">
                            Total entries: <span className="font-semibold text-slate-700">{ledgerData.total_entries || 0}</span>
                        </div>
                    )}
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Date</th>
                                <th className="text-left px-3 py-2 font-medium">Movement</th>
                                <th className="text-right px-3 py-2 font-medium">Delta</th>
                                <th className="text-right px-3 py-2 font-medium">Balance After</th>
                                <th className="text-left px-3 py-2 font-medium">Reason</th>
                                <th className="text-left px-3 py-2 font-medium">Remarks</th>
                                <th className="text-left px-3 py-2 font-medium">Actor</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(!ledgerData.ledger || ledgerData.ledger.length === 0) && (
                                <tr><td colSpan={7} className="text-center py-10 text-slate-400">No ledger entries for this leave type yet.</td></tr>
                            )}
                            {(ledgerData.ledger || []).map((row) => (
                                <tr key={row.id} className="border-t border-slate-100" data-testid={`lbe-ledger-row-${row.id}`}>
                                    <td className="px-3 py-2 text-xs text-slate-600">
                                        <div>{formatDate(row.movement_date)}</div>
                                        <div className="text-slate-400 text-[10px]">{formatDate(row.created_at)}</div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <Badge className={`text-xs ${MOVEMENT_TONE[row.movement_type] || "bg-slate-100"}`}>
                                            {row.movement_type}
                                        </Badge>
                                    </td>
                                    <td className={`px-3 py-2 text-right font-mono ${row.delta_days < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                                        {row.delta_days > 0 ? "+" : ""}{Number(row.delta_days).toFixed(2)}
                                    </td>
                                    <td className="px-3 py-2 text-right font-semibold">{Number(row.balance_after).toFixed(2)}</td>
                                    <td className="px-3 py-2 text-xs text-slate-600">
                                        {(() => {
                                            const detail = reasonsDetail.find((r) => r.code === row.reason);
                                            return detail?.label || REASON_LABELS[row.reason] || row.reason || "—";
                                        })()}
                                        {row.corrects_entry_id && (
                                            <div className="text-[10px] text-indigo-600 font-mono mt-0.5" title="Correction back-pointer">
                                                <ClockCounterClockwise size={10} className="inline mr-0.5" />
                                                corrects {row.corrects_entry_id.slice(-8)}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-700 max-w-xs truncate" title={row.remarks}>{row.remarks || "—"}</td>
                                    <td className="px-3 py-2 text-xs text-slate-500">{row.actor_email || "system"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Adjustment dialog */}
            <Dialog open={!!adjustDraft} onOpenChange={(o) => !o && setAdjustDraft(null)}>
                <DialogContent data-testid="lbe-adjust-dialog">
                    <DialogHeader>
                        <DialogTitle>Manual Adjustment · {adjustDraft?.leave_type_code}</DialogTitle>
                        <DialogDescription>Post a credit or debit entry to the leave ledger with a mandatory reason and remarks.</DialogDescription>
                    </DialogHeader>
                    {adjustDraft && (
                        <div className="space-y-3">
                            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-2">
                                Use positive delta for a credit, negative for a debit. The ledger entry is permanent and audit-stamped.
                            </div>
                            <div>
                                <label className="text-xs text-slate-600">Delta (days)</label>
                                <Input
                                    type="number" step="0.5"
                                    data-testid="lbe-adjust-delta"
                                    placeholder="e.g. 2 or -1.5"
                                    value={adjustDraft.delta_days}
                                    onChange={(e) => setAdjustDraft({ ...adjustDraft, delta_days: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="text-xs text-slate-600">Reason (required)</label>
                                <select
                                    data-testid="lbe-adjust-reason"
                                    className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                                    value={adjustDraft.reason}
                                    onChange={(e) => setAdjustDraft({ ...adjustDraft, reason: e.target.value })}
                                >
                                    <option value="">Select a reason…</option>
                                    {reasonsDetail.map((r) => (
                                        <option key={r.code} value={r.code} title={r.description}>{r.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs text-slate-600">Remarks (required, min 10 chars)</label>
                                <Textarea
                                    data-testid="lbe-adjust-remarks"
                                    rows={3}
                                    placeholder="Explain the adjustment for audit trail (≥ 10 chars)"
                                    value={adjustDraft.remarks}
                                    onChange={(e) => setAdjustDraft({ ...adjustDraft, remarks: e.target.value })}
                                />
                            </div>
                            {isHistoricalFy && (
                                <div className="rounded-md border border-rose-300 bg-rose-50 p-3 space-y-2" data-testid="lbe-adjust-historical-guard">
                                    <div className="text-xs font-semibold text-rose-800">Historical Balance Correction</div>
                                    <div className="text-xs text-rose-700 leading-relaxed">
                                        You are editing fiscal year <span className="font-mono">{fy}</span>, which is NOT the current FY. The correction will automatically cascade a <span className="font-mono">carry_forward_adjustment</span> entry into every downstream FY where this employee has an active balance. Prior payroll consumption is unaffected but may need reconciliation. <span className="font-semibold">This action is logged with severity=high.</span>
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-rose-800">Type <span className="font-mono">CORRECT HISTORICAL BALANCE</span> to proceed</label>
                                        <Input
                                            data-testid="lbe-adjust-historical-phrase"
                                            className="mt-1 font-mono text-xs"
                                            value={historicalConfirm}
                                            onChange={(e) => setHistoricalConfirm(e.target.value)}
                                            placeholder="CORRECT HISTORICAL BALANCE"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => { setAdjustDraft(null); setHistoricalConfirm(""); }} data-testid="lbe-adjust-cancel">Cancel</Button>
                        <Button onClick={submitAdjustment} data-testid="lbe-adjust-submit">Post Adjustment</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Iter 110 · P1.B — Balance-Correction dialog (per-bucket target editing) */}
            <Dialog open={!!correctDraft} onOpenChange={(o) => { if (!o) { setCorrectDraft(null); setHistoricalConfirm(""); } }}>
                <DialogContent data-testid="lbe-correct-dialog" className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Correct Balance Bucket · {correctDraft?.leave_type_code}</DialogTitle>
                        <DialogDescription>
                            Choose which balance bucket to correct. The dialog shows the current value; enter the target value and a delta will be computed and posted as an audit-stamped ledger entry.
                        </DialogDescription>
                    </DialogHeader>
                    {correctDraft && (
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs text-slate-600">Bucket to correct</label>
                                <select
                                    data-testid="lbe-correct-bucket"
                                    className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                                    value={correctDraft.bucket_key}
                                    onChange={(e) => {
                                        const key = e.target.value;
                                        const b = correctDraft._balance_row || {};
                                        const currentValue = Number(b[key] || 0);
                                        setCorrectDraft({
                                            ...correctDraft,
                                            bucket_key: key,
                                            current_value: currentValue,
                                            target_value: currentValue.toString(),
                                        });
                                    }}
                                >
                                    {BUCKET_TARGETS.map((t) => (
                                        <option key={t.key} value={t.key}>{t.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs text-slate-600">Current value</label>
                                    <Input
                                        data-testid="lbe-correct-current"
                                        readOnly
                                        value={Number(correctDraft.current_value || 0).toFixed(2)}
                                        className="bg-slate-50 font-mono"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs text-slate-600">Target value</label>
                                    <Input
                                        type="number" step="0.5" min="0"
                                        data-testid="lbe-correct-target"
                                        value={correctDraft.target_value}
                                        onChange={(e) => setCorrectDraft({ ...correctDraft, target_value: e.target.value })}
                                        className="font-mono"
                                    />
                                </div>
                            </div>
                            <div className="text-xs bg-slate-50 border border-slate-200 rounded-md p-2 flex items-center justify-between">
                                <span className="text-slate-600">Computed delta:</span>
                                <span className="font-mono font-semibold text-slate-800">
                                    {(() => {
                                        const bucket = BUCKET_TARGETS.find((b) => b.key === correctDraft.bucket_key);
                                        if (!bucket) return "—";
                                        const target = Number(correctDraft.target_value);
                                        const current = Number(correctDraft.current_value || 0);
                                        if (Number.isNaN(target)) return "—";
                                        const d = bucket.flip ? (current - target) : (target - current);
                                        return `${d > 0 ? "+" : ""}${d.toFixed(2)} days`;
                                    })()}
                                </span>
                            </div>
                            <div>
                                <label className="text-xs text-slate-600">Remarks (required, min 10 chars)</label>
                                <Textarea
                                    data-testid="lbe-correct-remarks"
                                    rows={3}
                                    placeholder="Explain why the target value differs from the current — auditors will read this"
                                    value={correctDraft.remarks}
                                    onChange={(e) => setCorrectDraft({ ...correctDraft, remarks: e.target.value })}
                                />
                            </div>
                            {isHistoricalFy && (
                                <div className="rounded-md border border-rose-300 bg-rose-50 p-3 space-y-2" data-testid="lbe-correct-historical-guard">
                                    <div className="text-xs font-semibold text-rose-800">Historical Balance Correction</div>
                                    <div className="text-xs text-rose-700 leading-relaxed">
                                        You are editing FY <span className="font-mono">{fy}</span> (not current). The correction will cascade into downstream FYs&apos; carry_forward_in balances. This is logged with severity=high.
                                    </div>
                                    <div>
                                        <label className="text-xs font-semibold text-rose-800">Type <span className="font-mono">CORRECT HISTORICAL BALANCE</span> to proceed</label>
                                        <Input
                                            data-testid="lbe-correct-historical-phrase"
                                            className="mt-1 font-mono text-xs"
                                            value={historicalConfirm}
                                            onChange={(e) => setHistoricalConfirm(e.target.value)}
                                            placeholder="CORRECT HISTORICAL BALANCE"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => { setCorrectDraft(null); setHistoricalConfirm(""); }} data-testid="lbe-correct-cancel">Cancel</Button>
                        <Button onClick={submitBalanceCorrection} data-testid="lbe-correct-submit">Post Correction</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Encashment dialog */}
            <Dialog open={!!encashDraft} onOpenChange={(o) => !o && setEncashDraft(null)}>
                <DialogContent data-testid="lbe-encash-dialog">
                    <DialogHeader>
                        <DialogTitle>Leave Encashment · {encashDraft?.leave_type_code}</DialogTitle>
                        <DialogDescription>Debit leave days and queue them for payroll processing (Phase E3).</DialogDescription>
                    </DialogHeader>
                    {encashDraft && (
                        <div className="space-y-3">
                            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-2">
                                Encashment debits the leave balance and is subject to policy caps (annual limit). Payroll integration is handled separately in Phase E3.
                            </div>
                            <div>
                                <label className="text-xs text-slate-600">Days to encash</label>
                                <Input
                                    type="number" step="0.5" min="0.5"
                                    data-testid="lbe-encash-days"
                                    value={encashDraft.days}
                                    onChange={(e) => setEncashDraft({ ...encashDraft, days: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="text-xs text-slate-600">Remarks (required, min 10 chars)</label>
                                <Textarea
                                    data-testid="lbe-encash-remarks"
                                    rows={3}
                                    placeholder="e.g. CL encashment approved by HR for Q4 cycle (≥ 10 chars)"
                                    value={encashDraft.remarks}
                                    onChange={(e) => setEncashDraft({ ...encashDraft, remarks: e.target.value })}
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setEncashDraft(null)} data-testid="lbe-encash-cancel">Cancel</Button>
                        <Button onClick={submitEncashment} data-testid="lbe-encash-submit">Post Encashment</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Opening balance dialog */}
            <Dialog open={!!openingDraft} onOpenChange={(o) => !o && setOpeningDraft(null)}>
                <DialogContent data-testid="lbe-opening-dialog">
                    <DialogHeader>
                        <DialogTitle>Set Opening Balance · {openingDraft?.leave_type_code}</DialogTitle>
                    </DialogHeader>
                    {openingDraft && (
                        <div className="space-y-3">
                            <div className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-md p-2">
                                Opening balance can only be set ONCE per (employee, leave type, fiscal year). To correct an already-posted opening balance, use the <span className="font-semibold">Correct Bucket</span> dialog (choose Opening Balance) — the correction is written as an <span className="font-mono">opening_balance_correction</span> ledger entry with a back-pointer to the original entry.
                            </div>
                            <div>
                                <label className="text-xs text-slate-600">Opening balance (days)</label>
                                <Input
                                    type="number" step="0.5" min="0"
                                    data-testid="lbe-opening-days"
                                    value={openingDraft.balance_days}
                                    onChange={(e) => setOpeningDraft({ ...openingDraft, balance_days: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="text-xs text-slate-600">Remarks (optional)</label>
                                <Textarea
                                    data-testid="lbe-opening-remarks"
                                    rows={2}
                                    value={openingDraft.remarks}
                                    onChange={(e) => setOpeningDraft({ ...openingDraft, remarks: e.target.value })}
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpeningDraft(null)} data-testid="lbe-opening-cancel">Cancel</Button>
                        <Button onClick={submitOpening} data-testid="lbe-opening-submit">Set Opening</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
